import React, { useState, useEffect } from 'react';
import { Loader2, ImageIcon, ZoomIn, X, AlertCircle } from 'lucide-react';
import { getCheckInImageUrl } from '../services/supabaseClient';

interface CheckInImageProps {
    imagePath: string;
    alt?: string;
    className?: string;
}

export const CheckInImage: React.FC<CheckInImageProps> = ({ imagePath, alt = 'Record', className = "" }) => {
    const [src, setSrc] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        let active = true;

        const loadUrl = async () => {
            try {
                setLoading(true);
                setError(false);
                const url = await getCheckInImageUrl(imagePath);
                if (active) {
                    setSrc(url);
                }
            } catch (err) {
                console.error("Failed to load image url", err);
                if (active) setError(true);
            } finally {
                if (active) setLoading(false);
            }
        };

        loadUrl();
        return () => { active = false; };
    }, [imagePath]);

    // Lock body scroll when fullscreen
    useEffect(() => {
        if (isFullScreen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isFullScreen]);

    if (loading) {
        return (
            <div className={`w-full h-40 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center animate-pulse ${className}`}>
                <Loader2 className="animate-spin text-primary-400" size={24} />
            </div>
        );
    }

    if (error || !src) {
        return (
            <div className={`w-full h-40 bg-slate-50 dark:bg-slate-900 rounded-xl flex flex-col items-center justify-center text-slate-400 border border-dashed border-slate-200 dark:border-slate-800 ${className}`}>
                <AlertCircle size={24} className="mb-2 opacity-50" />
                <span className="text-xs font-medium">图片加载失败</span>
            </div>
        );
    }

    return (
        <>
            <div
                className={`rounded-xl overflow-hidden mt-2 relative group/image cursor-zoom-in bg-slate-100 dark:bg-slate-900 ring-1 ring-black/5 hover:ring-primary-500/50 transition-all duration-300 ${className}`}
                onClick={() => setIsFullScreen(true)}
            >
                {!imageLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800 animate-pulse">
                        <ImageIcon className="text-slate-300" size={32} />
                    </div>
                )}
                <img
                    src={src}
                    alt={alt}
                    className={`w-full h-auto object-cover max-h-80 transform transition-all duration-700 ease-out group-hover/image:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0 scale-95'}`}
                    loading="lazy"
                    onLoad={() => setImageLoaded(true)}
                />

                {/* Overlay Info */}
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover/image:opacity-100 transition-opacity duration-300 flex justify-between items-end">
                    <div className="flex items-center gap-1.5 text-white/90">
                        <ZoomIn size={14} />
                        <span className="text-[11px] font-medium tracking-wide">点击放大</span>
                    </div>

                    {!src.startsWith('data:') && (
                        <div className="px-2 py-0.5 bg-white/20 hover:bg-white/30 text-white/90 text-[10px] rounded-full backdrop-blur-md transition-colors border border-white/20">
                            云端私密
                        </div>
                    )}
                </div>
            </div>

            {/* Full Screen Modal */}
            {isFullScreen && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-0 transition-all animate-fade-in"
                    onClick={() => setIsFullScreen(false)}
                >
                    <div className="absolute inset-0 bg-slate-950/95 backdrop-blur-xl"></div>

                    <button
                        className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all z-[101] border border-white/10 active:scale-95"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsFullScreen(false);
                        }}
                    >
                        <X size={24} />
                    </button>

                    <div className="relative w-full h-full flex items-center justify-center p-4">
                        <img
                            src={src}
                            alt={alt}
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-zoom-in"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </>
    );
};
